import { Light, LightFields, LightProperties } from '../../core/Light';
import { Vector3, Euler } from 'three';
import { forwardRef, useImperativeHandle, useRef } from 'react';
import * as THREE from 'three';
import { colors } from '@/constants/theme';
import { Field } from '@/types/core/Field';

export interface DirectionalLightProperties extends LightProperties {
  intensity: number;
  color: string;
}

// Create a reusable Directional Light component that can be used in both preview and world
const DirectionalLightComponent = forwardRef<THREE.DirectionalLight, { properties: DirectionalLightProperties; isPreview?: boolean }>((props, ref) => {
  const { properties, isPreview } = props;
  const { intensity, color, transform } = properties;
  const localRef = useRef<THREE.DirectionalLight>(null);

  // Forward the ref to parent
  useImperativeHandle(ref, () => localRef.current!, []);

  // In preview mode, use simplified transform
  const position = isPreview ? [3, 3, 3] : [transform.position.x, transform.position.y, transform.position.z];
  const rotation = isPreview ? [-Math.PI / 4, Math.PI / 4, 0] : [transform.rotation.x, transform.rotation.y, transform.rotation.z];

  return (
    <>
      <directionalLight
        ref={localRef}
        intensity={intensity}
        color={color}
        position={position as [number, number, number]}
        rotation={rotation as [number, number, number]}
        castShadow
      >
      </directionalLight>
      {!isPreview && (
        <mesh 
          scale={0.2}
          position={position as [number, number, number]}
          rotation={rotation as [number, number, number]}
        >
          <sphereGeometry />
          <meshBasicMaterial color={color} />
        </mesh>
      )}
      {isPreview && (
        <>
          {/* Ground plane to show shadows */}
          <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -2, 0]} receiveShadow>
            <planeGeometry args={[10, 10]} />
            <meshStandardMaterial color={colors.contrast} />
          </mesh>
          {/* Demo box to cast shadows */}
          <mesh position={[0, 0, 0]} rotation={[-Math.PI / 6, Math.PI / 6, 0]} castShadow receiveShadow>
            <boxGeometry args={[2, 2, 2]} />
            <meshPhysicalMaterial color={colors.primary} />
          </mesh>
          {/* Light indicator sphere */}
          <mesh position={position as [number, number, number]} scale={0.3}>
            <sphereGeometry />
            <meshBasicMaterial color={color} />
          </mesh>
        </>
      )}
    </>
  );
});

export const DirectionalLightItem: Light<DirectionalLightProperties> = {
  id: '',
  name: 'Directional Light',
  type: 'light',
  subtype: 'directional',
  icon: 'Sun',
  properties: {
    intensity: 1,
    color: '#ffffff',
    transform: {
      position: new Vector3(5, 5, 5),
      rotation: new Euler(),
      scale: new Vector3(1, 1, 1),
    },
  },
  fields: [
    ...LightFields,
    {
      key: 'properties.transform.position',
      type: 'vector3',
      label: 'Position',
      default: new Vector3(5, 5, 5),
      panel: 'transform',
    },
    {
      key: 'properties.transform.rotation',
      type: 'euler',
      label: 'Rotation',
      default: new Euler(),
      panel: 'transform',
    },
    {
      key: 'properties.transform.scale',
      type: 'vector3',
      label: 'Scale',
      default: new Vector3(1, 1, 1),
      panel: 'transform',
    },
  ],
  render: (properties: DirectionalLightProperties, isPreview: boolean, ref: React.RefObject<THREE.Object3D | null>) => {
    return <DirectionalLightComponent properties={properties} isPreview={isPreview} ref={ref as React.RefObject<THREE.DirectionalLight>} />;
  }
}; 